/**
 * Inspection Release Notes — QMS list (create IRN remains on delivery note).
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Card,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  Button,
} from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useIRNs } from '@/hooks/useQCWorkflow';
import type { InspectionReleaseNote } from '@/services/qc-workflow.service';

const { Title, Text } = Typography;
const { Option } = Select;

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    Draft: 'default',
    'Pending Approval': 'processing',
    Approved: 'success',
    Rejected: 'error',
    Cancelled: 'default',
  };
  return colors[status] || 'default';
}

export const InspectionReleaseNotesPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchText, setSearchText] = useState('');

  const filters = useMemo(() => {
    if (statusFilter === 'all') return undefined;
    return { status: statusFilter };
  }, [statusFilter]);

  const { data: irns = [], isLoading } = useIRNs(filters);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return irns;
    return irns.filter((row) => {
      const woNum =
        row.workOrder?.work_order_number?.toLowerCase() ??
        row.deliveryNote?.workOrder?.work_order_number?.toLowerCase() ??
        '';
      const dn = row.deliveryNote?.delivery_note_number?.toLowerCase() ?? '';
      const fir = row.fir?.fir_number?.toLowerCase() ?? '';
      return (
        row.irn_number.toLowerCase().includes(q) ||
        woNum.includes(q) ||
        dn.includes(q) ||
        fir.includes(q)
      );
    });
  }, [irns, searchText]);

  const columns: ColumnsType<InspectionReleaseNote> = [
    {
      title: 'IRN number',
      dataIndex: 'irn_number',
      key: 'irn_number',
      render: (v: string, r) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/qms/inspection-release-notes/${r.id}`)}>
          {v}
        </Button>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (s: string) => <Tag color={getStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Release qty',
      dataIndex: 'release_quantity',
      key: 'release_quantity',
      width: 120,
      render: (q: number) =>
        q != null && !Number.isNaN(Number(q)) ? Number(q).toFixed(2) : '—',
    },
    {
      title: 'Work order',
      key: 'wo',
      width: 140,
      render: (_, r) => {
        const id = r.work_order_id || r.workOrder?.id || r.deliveryNote?.workOrder?.id;
        const num = r.workOrder?.work_order_number || r.deliveryNote?.workOrder?.work_order_number;
        if (!id || !num) return '—';
        return (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/mfg/work-orders/${id}`)}>
            {num}
          </Button>
        );
      },
    },
    {
      title: 'Delivery note',
      key: 'dn',
      width: 140,
      render: (_, r) => {
        const id = r.delivery_note_id || r.deliveryNote?.id;
        const num = r.deliveryNote?.delivery_note_number;
        if (!id || !num) return '—';
        return (
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/growth/delivery-notes/${id}`)}>
            {num}
          </Button>
        );
      },
    },
    {
      title: 'FIR',
      key: 'fir',
      width: 120,
      render: (_, r) => {
        const id = r.fir_id || r.fir?.id;
        const num = r.fir?.fir_number;
        if (!id || !num) return '—';
        return (
          <Button
            type="link"
            style={{ padding: 0 }}
            onClick={() => navigate(`/qms/final-inspection-reports/${id}?source=fir_request`)}
          >
            {num}
          </Button>
        );
      },
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 130,
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <Button type="primary" ghost size="small" icon={<EyeOutlined />} onClick={() => navigate(`/qms/inspection-release-notes/${r.id}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Inspection release notes
          </Title>
          <Text type="secondary">Quality approval for released quantities. New IRNs are created from a delivery note in Sales.</Text>
        </div>

        <Alert
          type="info"
          showIcon
          message="Create an IRN from the delivery note"
          description="Open Sales → Delivery notes, open the note, and use the Inspection Release Note tab to create an IRN for that shipment."
        />

        <Card>
          <Space wrap style={{ marginBottom: 16 }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search IRN, WO, DN, FIR…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 280 }}
            />
            <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 200 }}>
              <Option value="all">All statuses</Option>
              <Option value="Draft">Draft</Option>
              <Option value="Pending Approval">Pending approval</Option>
              <Option value="Approved">Approved</Option>
              <Option value="Rejected">Rejected</Option>
              <Option value="Cancelled">Cancelled</Option>
            </Select>
          </Space>

          <Table<InspectionReleaseNote>
            rowKey="id"
            loading={isLoading}
            columns={columns}
            dataSource={filtered}
            pagination={{ pageSize: 20, showSizeChanger: true }}
          />
        </Card>
      </Space>
    </div>
  );
};
